import { Select, Option } from '@mm/components';
import { DemographicAnalyticsKeyType } from '@mm/state/interfaces/botAnalytics';
import {
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, { useState } from 'react';
import { UpdateFilterParamsType } from '../../../Contacts/MyLeadsFiltersPanel/use-filter-params';
import styles from '../DemographicAnalytics.module.css';

type Props = {
  label: string;
  options: Option[];
  filterKey: DemographicAnalyticsKeyType;
  filterType: MyLeadsFilterTypes;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (value: MyLeadsSearchQuery) => void;
  getUpdatedFilterParams: (args: UpdateFilterParamsType) => MyLeadsSearchQuery;
  disabled?: boolean;
  isLoading?: boolean;
};

export const DemographicFilterSelect = ({
  filterKey,
  filterType,
  label,
  options,
  filterParams,
  setFilterParams,
  getUpdatedFilterParams,
  disabled,
  isLoading,
}: Props) => {
  const defaultSelectedOptions = filterParams.query?.variables?.[
    filterKey
  ]?.map(opt => {
    const matchingOption = options.find(o => o.id === opt);
    if (matchingOption) {
      return { id: matchingOption.id, label: matchingOption.label };
    }
    return { id: '', label: '' };
  });

  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    defaultSelectedOptions || [],
  );

  const onOptionsChange = (v: Option[]) => {
    setSelectedOptions(v);
    if (!v.length) {
      onClose(v.map(o => o.id) as string[]);
    }
  };

  const onClose = (value: string[] | number[]) => {
    const lowercasedValue = value.map(v => v?.toLowerCase());
    const paramsEqual =
      defaultSelectedOptions || lowercasedValue.length
        ? isEqual(
            lowercasedValue,
            defaultSelectedOptions?.map(o => o.id),
          )
        : true;

    if (!paramsEqual) {
      const updatedQuery = getUpdatedFilterParams({
        key: filterKey,
        value: lowercasedValue,
        isExisting: false,
        type: filterType,
        skipPrevValues: true,
      });
      setFilterParams(updatedQuery);
    }
  };

  return (
    <Select
      className={classNames(
        styles.audienceSelector,
        styles.demographicFilterSelect,
      )}
      label={label}
      selected={selectedOptions}
      options={options}
      onChange={onOptionsChange}
      onClose={() => onClose(selectedOptions.map(v => v.id) as string[])}
      disabled={disabled}
      isLoading={isLoading}
      disableClickOutsideExceptions
    />
  );
};
